import React from 'react'
import { KTSVG } from '../../../../_metronic/helpers'
import { DailyPositionReportRecord } from '../core/_dprModels'

const resolveLubeClosingStock = (row: any) => {
  const explicitClosing = row.closingStock ?? row.closing
  if (explicitClosing !== undefined && explicitClosing !== null && explicitClosing !== '' && explicitClosing !== '—') return explicitClosing

  const openingStock = parseFloat(row.openingStock)
  if (!isNaN(openingStock)) {
    const received = parseFloat(row.received) || 0
    const consumed = parseFloat(row.consumedByVessel) || 0
    return (openingStock + received - consumed).toFixed(3).replace(/\.?0+$/, '')
  }
  return undefined
}

interface Props {
  isOpen: boolean
  onClose: () => void
  data: DailyPositionReportRecord | null
}

const ViewDprModal: React.FC<Props> = ({ isOpen, onClose, data }) => {
  if (!isOpen || !data) return null

  const renderEmptyRow = (colSpan: number, message: string) => (
    <tr>
      <td className='text-center text-muted' colSpan={colSpan}>
        {message}
      </td>
    </tr>
  )

  const renderStatusBadge = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return <span className='badge badge-light-secondary'>Draft</span>
      case 'SUBMITTED':
        return <span className='badge badge-light-warning'>Submitted</span>
      case 'REVIEWED':
        return <span className='badge badge-light-success'>Reviewed</span>
      case 'REJECTED':
        return <span className='badge badge-light-danger'>Rejected</span>
      default:
        return <span className='badge badge-light'>{status}</span>
    }
  }

  const getExhTempMinValue = (row: {
    unit1?: string
    unit2?: string
    unit3?: string
    unit4?: string
    unit5?: string
    unit6?: string
    unit7?: string
    unit8?: string
  }) => {
    const values = [row.unit1, row.unit2, row.unit3, row.unit4, row.unit5, row.unit6, row.unit7, row.unit8]
      .map((value) => String(value ?? '').trim())
      .filter((value) => value !== '')
      .map((value) => parseFloat(value))
      .filter((value) => Number.isFinite(value))

    if (values.length === 0) return '—'

    const average = values.reduce((sum, value) => sum + value, 0) / values.length
    return Number(average.toFixed(3)).toString()
  }

  const normalizeExhTempLabel = (label?: string) => {
    const normalized = String(label ?? '').trim().toUpperCase().replace(/\s+/g, ' ')
    switch (normalized) {
      case 'M.E PORT':
      case 'M.E. PORT':
        return 'M/E(P)'
      case 'M.E LOG. PORT':
      case 'M.E LOG PORT':
      case 'M.E. LOG. PORT':
        return 'M/E(S)'
      case 'M.E STBD':
      case 'M.E. STBD':
        return 'AUX(1)'
      case 'M.E LOG. STBD':
      case 'M.E LOG STBD':
      case 'M.E. LOG. STBD':
        return 'AUX(2)'
      default:
        return label || '—'
    }
  }

  return (
    <div className='modal-overlay'>
      <div
        className='modal-content'
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '80rem' }}
      >
        {/* Header */}
        <div className='custom-modal-header d-flex justify-content-between align-items-center'>
          <h5 className='m-0'>View Daily Progress Report</h5>
          <button className='close-btn' onClick={onClose}>
            <KTSVG path='/media/map/x.svg' className='svg-icon-2x' />
          </button>
        </div>

        <style>{`
          .dpr-modal-table-scope table thead th {
            background-color: #eaf3ff !important;
          }
          .dpr-modal-table-scope table tbody td,
          .dpr-modal-table-scope table tbody th {
            background-color: #ffffff !important;
          }
          .dpr-modal-table-scope table tbody tr:nth-child(odd) td,
          .dpr-modal-table-scope table tbody tr:nth-child(odd) th,
          .dpr-modal-table-scope table tbody tr:nth-child(even) td,
          .dpr-modal-table-scope table tbody tr:nth-child(even) th {
            background-color: #ffffff !important;
          }
        `}</style>

        <div className='custom-modal-body dpr-modal-table-scope' style={{ maxHeight: '70vh', overflowY: 'auto' }}>


          {/* ── 1. DATE & LOCATION ── */}
          <h6 className='text-primary mb-3'>1. DATE &amp; LOCATION</h6>
          <div className='table-responsive mb-4'>
            <table className='table table-bordered table-sm align-middle mb-0'>
              <tbody>
                <tr>
                  <td className='fw-bold bg-light' style={{width: '120px'}}>Date :</td>
                  <td>{data.dlDate || '—'}</td>
                  <td className='fw-bold bg-light' style={{width: '120px'}}>Location:</td>
                  <td className='fw-bold'>{data.dlLocation || '—'}</td>
                </tr>
                <tr>
                  <td className='fw-bold bg-light'>Time :</td>
                  <td>{data.dlTime || '—'}</td>
                  <td className='fw-bold bg-light'>Position:</td>
                  <td>{data.dlPosition || '—'}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <hr />

          {/* ── 2. FO CONSUMPTION ── */}
          <h6 className='text-primary mb-3'>2.DAILY ACTIVITY F.O. CONSUMPTION</h6>
          <div className='table-responsive mb-4'>
            <table className='table table-bordered table-sm align-middle text-center mb-0' style={{ fontSize: '0.85rem' }}>
              <thead className='table-light'>
                <tr>
                  <th rowSpan={2} className='align-middle'>DATE</th>
                  <th colSpan={2}>Above 6 Kts</th>
                  <th colSpan={2}>STEAMING (up to 6 Kts)</th>
                  <th colSpan={2}>PLATFORM/DP</th>
                  <th colSpan={2}>AT ANCHORAGE</th>
                  <th colSpan={2}>AT BERTH</th>
                  <th rowSpan={2} className='align-middle'>TOTAL F.O.<br/>CONS (IN KL)</th>
                  <th rowSpan={2} className='align-middle'>TOTAL HR</th>
                  <th rowSpan={2} className='align-middle'>TOTAL HR</th>
                  <th rowSpan={2} className='align-middle'>REMARKS</th>
                </tr>
                <tr>
                  <th>HRS</th>
                  <th>F.O CONS (KL)</th>
                  <th>HRS</th>
                  <th>F.O CONS (KL)</th>
                  <th>HRS</th>
                  <th>F.O CONS (KL)</th>
                  <th>HRS</th>
                  <th>F.O CONS (KL)</th>
                  <th>HRS</th>
                  <th>F.O CONS (KL)</th>
                </tr>
              </thead>
              <tbody>
                {(data.foConsumptionRows || []).map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.date || '—'}</td>
                    <td>{row.above6Hrs || '—'}</td>
                    <td>{row.above6Cons || '—'}</td>
                    <td>{row.steamingHrs || '—'}</td>
                    <td>{row.steamingCons || '—'}</td>
                    <td>{row.platformDpHrs || '—'}</td>
                    <td>{row.platformDpCons || '—'}</td>
                    <td>{row.atAnchorageHrs || '—'}</td>
                    <td>{row.atAnchorageCons || '—'}</td>
                    <td>{row.atBerthHrs || '—'}</td>
                    <td>{row.atBerthCons || '—'}</td>
                    <td>{row.totalFoCons || '—'}</td>
                    <td>{row.totalHr1 || '—'}</td>
                    <td>{row.totalHr2 || '—'}</td>
                    <td>{row.remarks || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <hr />

          {/* ── 2.1 Machinery FO Consumption ── */}
          <h6 className='text-primary mb-3'>2.1 Machinery F.O. Consumption</h6>
          <div className='table-responsive mb-4'>
            <table className='table table-bordered table-sm align-middle text-center mb-0'>
              <thead className='table-light'>
                <tr>
                  <th>Machinery</th>
                  <th>FO Consumption (KL)</th>
                </tr>
              </thead>
              <tbody>
                {(data.machineryFoConsumptionRows || []).length
                  ? (data.machineryFoConsumptionRows || []).map((row, idx) => (
                      <tr key={idx}>
                        <td className='text-center'>{row.machinery || '—'}</td>
                        <td>{row.foConsumption || '—'}</td>
                      </tr>
                    ))
                  : renderEmptyRow(2, 'No machinery FO consumption rows')}
              </tbody>
            </table>
          </div>

          <hr />

          {/* ── Section 2: Activity ── */}
          <h6 className='text-primary mb-3'>3. Activity</h6>
          <div className='table-responsive mb-4'>
            <table className='table table-bordered table-sm'>
              <thead className='table-light'>
                <tr>
                  <th>Local Time</th>
                  <th>Activity Description</th>
                  <th>Position</th>
                </tr>
              </thead>
              <tbody>
                {data.activities.map((act, idx) => (
                  <tr key={idx}>
                    <td className='text-nowrap'>
                      {act.localTimeFrom}–{act.localTimeTo}
                    </td>
                    <td>{act.description || '—'}</td>
                    <td>{act.position || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <hr />

          {/* ── Section 3: Activity Summary ── */}
          <h6 className='text-primary mb-3'>4. Activity Summary</h6>
          <div className='table-responsive mb-4'>
            <table className='table table-bordered table-sm'>
              <thead className='table-light'>
                <tr>
                  <th></th>
                  <th className='text-center'>Speeds ≤6 kn</th>
                  <th className='text-center'>Speeds &gt;6 kn</th>
                  <th className='text-center'>In DP Mode</th>
                  <th className='text-center'>At Anchorage / Berth</th>
                  <th className='text-center'>Down Time</th>
                  <th className='text-center'>Others</th>
                  <th className='text-center'>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className='fw-bold'>Time (hh:mm)</td>
                  <td className='text-center'>{data.activitySummary.speedsUpto6 || '—'}</td>
                  <td className='text-center'>{data.activitySummary.speedsAbove6 || '—'}</td>
                  <td className='text-center'>{data.activitySummary.inDpMode || '—'}</td>
                  <td className='text-center'>{data.activitySummary.atAnchorageBerth || '—'}</td>
                  <td className='text-center'>{data.activitySummary.downTime || '—'}</td>
                  <td className='text-center'>{data.activitySummary.othersSpecify || '—'}</td>
                  <td className='text-center fw-bold'>{data.activitySummary.total || '—'}</td>
                </tr>
                <tr>
                  <td className='fw-bold'>Fuel consumed in 24 hrs</td>
                  <td colSpan={7} className='text-center'>
                    {data.activitySummary.fuelConsumedIn24Hrs || '—'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <hr />

          {/* ── Section 4: ROB Status ── */}
          <h6 className='text-primary mb-3'>5. ROB Status</h6>
          <div className='table-responsive mb-4'>
            <table className='table table-bordered table-sm'>
              <thead className='table-light'>
                <tr>
                  <th>Commodity</th>
                  <th className='text-center'>Opening Stock</th>
                  <th className='text-center'>Received</th>
                  <th className='text-center'>Del. to other Vessel</th>
                  <th className='text-center'>Del. to Platforms</th>
                  <th className='text-center'>Consumed by Vessel</th>
                  <th className='text-center'>Closing Stock</th>
                </tr>
              </thead>
              <tbody>
                {(data.robStatus || []).map((row, idx) => (
                  <tr key={idx}>
                    <td className='fw-bold'>{row.commodity}</td>
                    <td className='text-center'>{row.openingStock}</td>
                    <td className='text-center'>{row.received}</td>
                    <td className='text-center'>{row.deliveredToOtherVessel}</td>
                    <td className='text-center'>{row.deliveredToPlatforms}</td>
                    <td className='text-center'>{row.consumedByVessel}</td>
                    <td className='text-center'>{row.closingStock}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <hr />

          {/* ── Section 4.1: Lube Oil Status ── */}
          <h6 className='text-primary mb-3'>5.1. Lube Oil Status</h6>
          <div className='table-responsive mb-4'>
            <table className='table table-bordered table-sm'>
              <thead className='table-light'>
                <tr>
                  <th className='text-center'>Grades</th>
                  <th className='text-center'>Opening Stock</th>
                  <th className='text-center'>Received</th>
                  <th className='text-center'>Consumed by Vessel</th>
                  <th className='text-center'>Closing Stock</th>
                </tr>
              </thead>
              <tbody>
                {(data.lubeOilStatusRows || []).length
                  ? (data.lubeOilStatusRows || []).map((row, idx) => (
                      <tr key={idx}>
                        <td className='fw-bold'>{row.machinery || '—'}</td>
                        <td className='text-center'>{row.openingStock || '—'}</td>
                        <td className='text-center'>{row.received || '—'}</td>
                        <td className='text-center'>{row.consumedByVessel || '—'}</td>
                        <td className='text-center'>{resolveLubeClosingStock(row) || '—'}</td>
                      </tr>
                    ))
                  : renderEmptyRow(5, 'No lube oil status rows')}
              </tbody>
            </table>
          </div>

          <hr />

          {/* ── Section 5: Weather ── */}
          <h6 className='text-primary mb-3'>6. Weather</h6>
          <div className='table-responsive mb-3'>
            <table className='table table-bordered table-sm'>
              <thead className='table-light'>
                <tr>
                  <th>Local Time</th>
                  <th className='text-center'>Wind Force</th>
                  <th className='text-center'>Wind Spd</th>
                  <th className='text-center'>Direction</th>
                  <th className='text-center'>Sea/Swell Ht</th>
                  <th className='text-center'>Direction</th>
                  <th className='text-center'>Current Rate</th>
                  <th className='text-center'>Direction</th>
                  <th className='text-center'>Baro</th>
                  <th className='text-center'>Sky</th>
                  <th className='text-center'>Vis</th>
                  <th className='text-center'>TEMP</th>
                </tr>
              </thead>
              <tbody>
                {(data.weatherRows || []).map((row, idx) => (
                  <tr key={idx}>
                    <td className='fw-bold text-center'>{row.localTime}</td>
                    <td className='text-center'>{row.windForce || '—'}</td>
                    <td className='text-center'>{row.windSpd || '—'}</td>
                    <td className='text-center'>{row.direction || '—'}</td>
                    <td className='text-center'>{row.seaSwellHt || '—'}</td>
                    <td className='text-center'>{row.swellDirection || '—'}</td>
                    <td className='text-center'>{row.currentRate || '—'}</td>
                    <td className='text-center'>{row.currentDirection || '—'}</td>
                    <td className='text-center'>{row.baro || '—'}</td>
                    <td className='text-center'>{row.sky || '—'}</td>
                    <td className='text-center'>{row.vis || '—'}</td>
                    <td className='text-center'>{row.temp || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.tides && (
            <div className='row g-3 mb-4'>
              <div className='col-md-12'>
                <table className='table table-bordered table-sm' style={{ maxWidth: '600px' }}>
                  <thead className='table-light'>
                    <tr>
                      <th></th>
                      <th className='text-center'>AM-LW</th>
                      <th className='text-center'>AM-HW</th>
                      <th className='text-center'>PM-LW</th>
                      <th className='text-center'>PM-HW</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className='fw-bold'>Tides (Time)</td>
                      <td className='text-center'>{data.tides.amLw || '—'}</td>
                      <td className='text-center'>{data.tides.amHw || '—'}</td>
                      <td className='text-center'>{data.tides.pmLw || '—'}</td>
                      <td className='text-center'>{data.tides.pmHw || '—'}</td>
                    </tr>
                    <tr>
                      <td className='fw-bold'>Heights</td>
                      <td className='text-center'>{data.tides.amLwHeight || '—'}</td>
                      <td className='text-center'>{data.tides.amHwHeight || '—'}</td>
                      <td className='text-center'>{data.tides.pmLwHeight || '—'}</td>
                      <td className='text-center'>{data.tides.pmHwHeight || '—'}</td>
                    </tr>
                  </tbody>
                </table>
                <div className='d-flex gap-4 mt-2'>
                  <span><strong>Sunrise:</strong> {data.tides.sunrise || '—'}</span>
                  <span><strong>Sunset:</strong> {data.tides.sunset || '—'}</span>
                </div>
              </div>
            </div>
          )}

          <hr />

          {/* ── Section 6: Downtime ── */}
          <h6 className='text-primary mb-3'>7. Downtime</h6>
          <div className='table-responsive mb-4'>
            <table className='table table-bordered table-sm'>
              <thead className='table-light'>
                <tr>
                  <th className='text-center'>From</th>
                  <th className='text-center'>To</th>
                  <th className='text-center'>Today</th>
                  <th className='text-center'>Previous</th>
                  <th className='text-center'>Total (dd:hh:mm)</th>
                  <th>Reasons / Remarks</th>
                </tr>
              </thead>
              <tbody>
                {(data.downtimeRows || []).map((row, idx) => (
                  <tr key={idx}>
                    <td className='text-center'>{row.from || 'NIL'}</td>
                    <td className='text-center'>{row.to || 'NIL'}</td>
                    <td className='text-center'>{row.today || 'NIL'}</td>
                    <td className='text-center'>{row.previous || 'NIL'}</td>
                    <td className='text-center'>{row.total || 'NIL'}</td>
                    <td>{row.reasonsRemarks || 'NIL'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <hr />

          {/* ── Section 7: P.O.B ── */}
          <h6 className='text-primary mb-3'>8. P.O.B (Persons On Board)</h6>
          <div className='table-responsive mb-4'>
            <table className='table table-bordered table-sm'>
              <thead className='table-light'>
                <tr>
                  <th className='text-center'>S.No</th>
                  <th>Company</th>
                  <th className='text-center'>Previous</th>
                  <th className='text-center'>Pax-In</th>
                  <th className='text-center'>Pax-Out</th>
                  <th className='text-center'>Total</th>
                  <th className='text-center'>Breakfast</th>
                  <th className='text-center'>Lunch</th>
                  <th className='text-center'>Dinner</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {(data.pobRows || []).map((row, idx) => (
                  <tr key={idx}>
                    <td className='text-center'>{row.sNo}</td>
                    <td className='fw-bold'>{row.company}</td>
                    <td className='text-center'>{row.previous}</td>
                    <td className='text-center'>{row.paxIn}</td>
                    <td className='text-center'>{row.paxOut}</td>
                    <td className='text-center'>{row.total}</td>
                    <td className='text-center'>{row.mealsBreakfast}</td>
                    <td className='text-center'>{row.mealsLunch}</td>
                    <td className='text-center'>{row.mealsDinner}</td>
                    <td>{row.remarks || '—'}</td>
                  </tr>
                ))}
                <tr className='table-warning fw-bold'>
                  <td></td>
                  <td>Total</td>
                  <td className='text-center'>{(data.pobRows || []).reduce((s, r) => s + (parseInt(r.previous) || 0), 0)}</td>
                  <td className='text-center'>{(data.pobRows || []).reduce((s, r) => s + (parseInt(r.paxIn) || 0), 0)}</td>
                  <td className='text-center'>{(data.pobRows || []).reduce((s, r) => s + (parseInt(r.paxOut) || 0), 0)}</td>
                  <td className='text-center'>{(data.pobRows || []).reduce((s, r) => s + (parseInt(r.total) || 0), 0)}</td>
                  <td className='text-center'>{(data.pobRows || []).reduce((s, r) => s + (parseInt(r.mealsBreakfast) || 0), 0)}</td>
                  <td className='text-center'>{(data.pobRows || []).reduce((s, r) => s + (parseInt(r.mealsLunch) || 0), 0)}</td>
                  <td className='text-center'>{(data.pobRows || []).reduce((s, r) => s + (parseInt(r.mealsDinner) || 0), 0)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>

          <hr />

          {/* ── Section 8: Statistics & Safety Checks ── */}
          <h6 className='text-primary mb-3'>9. Statistics &amp; Safety Checks</h6>
          <div className='row mb-3'>
            <div className='col-md-6'>
              <div className='table-responsive'>
                <table className='table table-bordered table-sm'>
                  <thead className='table-light'>
                    <tr>
                      <th>KPI's</th>
                      <th className='text-center'>Prev Day</th>
                      <th className='text-center'>Today</th>
                      <th className='text-center'>Cumulative</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.safetyLeft || []).map((row, idx) => (
                      <tr key={idx}>
                        <td className='small'>{row.particulars}</td>
                        <td className='text-center'>{row.previousDay}</td>
                        <td className='text-center'>{row.today}</td>
                        <td className='text-center'>{row.cumulative}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className='col-md-6'>
              <div className='table-responsive'>
                <table className='table table-bordered table-sm'>
                  <thead className='table-light'>
                    <tr>
                      <th>Meetings and Inspections</th>
                      <th className='text-center'>Prev Day</th>
                      <th className='text-center'>Today</th>
                      <th className='text-center'>Cumulative</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.safetyRight || []).map((row, idx) => (
                      <tr key={idx}>
                        <td className='small'>{row.particulars}</td>
                        <td className='text-center'>{row.previousDay}</td>
                        <td className='text-center'>{row.today}</td>
                        <td className='text-center'>{row.cumulative}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <h6 className='mb-2'>Key Equipment Status</h6>
          <div className='table-responsive mb-4'>
            <table className='table table-bordered table-sm' style={{ maxWidth: '400px' }}>
              <thead className='table-light'>
                <tr>
                  <th>Equipment</th>
                  <th className='text-center'>Standby (Y/N)</th>
                </tr>
              </thead>
              <tbody>
                {(data.equipmentRows || []).map((row, idx) => (
                  <tr key={idx}>
                    <td className='small'>{row.equipment}</td>
                    <td className='text-center'>{row.standbyYN}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <hr />

          {/* ── Section 9: Passenger Details ── */}
          <h6 className='text-primary mb-3'>10. PASSENGER DETAILS</h6>
          <div className='table-responsive mb-4'>
            <table className='table table-bordered table-sm'>
              <thead className='table-light'>
                <tr>
                  <th className='text-center' style={{ width: '60px' }}>Sr.No</th>
                  <th>Name</th>
                  <th>Company</th>
                  <th>Position</th>
                  <th className='text-center' style={{ width: '120px' }}>DOJ</th>
                </tr>
              </thead>
              <tbody>
                {(data.passengerRows || []).map((row, idx) => (
                  <tr key={idx}>
                    <td className='text-center'>{row.srNo || '—'}</td>
                    <td>{row.name || '—'}</td>
                    <td>{row.company || '—'}</td>
                    <td>{row.position || '—'}</td>
                    <td className='text-center'>{row.doj || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <hr />

          {/* ── Section 10: Engine Running Hours ── */}
          <h6 className='text-primary mb-3'>11. ENGINE RUNNING HOURS</h6>
          <div className='table-responsive mb-4'>
            <table className='table table-bordered table-sm align-middle text-center mb-0'>
              <thead className='table-light'>
                <tr>
                  <th style={{ width: '150px' }}>LABEL</th>
                  <th>OPENING R/Hr</th>
                  <th>DAILY R/Hr</th>
                  <th>TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {(data.engineRunningHours || []).map((row, idx) => (
                  <tr key={idx}>
                    <td className='bg-light fw-bold small'>{row.label}</td>
                    <td>{row.openingHours || '0'}</td>
                    <td>{row.dailyHours || '0'}</td>
                    <td className='fw-bold'>{row.totalHours || '0'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <hr />

          {/* ── Section 11: Engine Parameter ── */}
          <h6 className='text-primary mb-3'>12. ENGINE PARAMETER</h6>
          <div className='table-responsive mb-4'>
            <table className='table table-bordered table-sm align-middle text-center mb-0'>
              <thead className='table-light'>
                <tr>
                  <th style={{ width: '150px' }}>LABEL</th>
                  <th>LO PRES</th>
                  <th>JCW COOLER</th>
                  <th>LO Temp cooler</th>
                </tr>
              </thead>
              <tbody>
                {(data.engineTemp?.params || []).map((row, idx) => (
                  <tr key={idx}>
                    <td className='bg-light fw-bold small'>{row.label}</td>
                    <td>{row.loPres || '—'}</td>
                    <td>{row.jcwCooler || '—'}</td>
                    <td>{row.loTempCooler || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <hr />

          {/* ── Section 12: Exhaust Temperature ── */}
          <h6 className='text-primary mb-3'>13. EXHAUST TEMPERATURE of all units</h6>
          <div className='table-responsive mb-4'>
            <table className='table table-bordered table-sm align-middle text-center mb-0'>
              <thead className='table-light'>
                <tr>
                  <th style={{ width: '150px' }}>LABEL</th>
                  <th>1</th>
                  <th>2</th>
                  <th>3</th>
                  <th>4</th>
                  <th>5</th>
                  <th>6</th>
                  <th>7</th>
                  <th>8</th>
                  <th>Min</th>
                </tr>
              </thead>
              <tbody>
                {(data.engineTemp?.exhTemps || []).map((row, idx) => (
                  <tr key={idx}>
                    <td className='bg-light fw-bold small text-nowrap'>{normalizeExhTempLabel(row.label)}</td>
                    <td>{row.unit1 || '—'}</td>
                    <td>{row.unit2 || '—'}</td>
                    <td>{row.unit3 || '—'}</td>
                    <td>{row.unit4 || '—'}</td>
                    <td>{row.unit5 || '—'}</td>
                    <td>{row.unit6 || '—'}</td>
                    <td>{row.unit7 || '—'}</td>
                    <td>{row.unit8 || '—'}</td>
                    <td className='fw-bold'>{getExhTempMinValue(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className='d-flex justify-content-end p-3'>
          <button className='btn btn-secondary' onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default ViewDprModal
